import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { ROUTES } from "@via/utils/constants.tsx";
import {
  type ErrorNotification,
  type ForgotPasswordPayload,
  type LoginPayload,
  type SetNewPasswordPayload,
  type SignUpPayload,
  type VerifyPasswordResetTokenPayload,
} from "@via/utils/interfaces.tsx";
import {
  ChangeUserPasswordRequestBody,
  RegistrationType,
  OnboardingStep,
  UserInfoResponse,
} from "common";
import { useNavigate } from "react-router-dom";
import { isBackendRoute } from "@via/utils/post-login-redirect";
import { codesReminderPath } from "@via/api/mfa-destination";

export const useSignUp = (): any => {
  const { setUser } = useAuth();
  const { setNotification, NOTIFICATION } = useNotifications();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (data: SignUpPayload) => {
      const payload = { ...data, identityType: 1 };
      const response = await axios.post("/api/auth/v1/signup", payload);
      return response.data;
    },
    onSuccess: (data): void => {
      setUser(data);
      // Backend tracks USER_SIGNUP event
      navigate(ROUTES.ACTIVITY);
      setNotification({
        id: "signup-success",
        message: "Sign Up Successful",
        details: "Welcome to your new account",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: error => {
      throw error;
    },
  });
};

export const useLogin = (options?: {
  redirect?: string | null;
  /**
   * Where the code screen returns to when a second factor is owed, in place of
   * `redirect`. The invite sign-in pages pass themselves (path and query), so
   * the invitation is accepted once the session exists.
   */
  mfaResume?: string | null;
}): any => {
  const queryClient = useQueryClient();
  const { setUser } = useAuth();
  const { setNotification, NOTIFICATION } = useNotifications();
  const navigate = useNavigate();
  const redirectUrl = options?.redirect;
  const mfaResume = options?.mfaResume || redirectUrl;
  return useMutation({
    mutationFn: async (data: LoginPayload) => {
      const payload = { ...data, identityType: 1 };
      const response = await axios.post("/api/auth/v1/login", payload);
      return response.data;
    },
    onSuccess: (data): void => {
      // The first factor passed but a second one is owed: no user yet.
      if (data?.mfaRequired) {
        navigate(
          mfaResume
            ? `${ROUTES.LOGIN_MFA}?redirect=${encodeURIComponent(mfaResume)}`
            : ROUTES.LOGIN_MFA
        );
        return;
      }
      setUser(data);
      // Backend tracks USER_LOGIN event
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/user"],
      });
      setTimeout(() => {
        // Self-registered users who have not finished onboarding
        const onboardingPending =
          data.registrationType === RegistrationType.Self &&
          data.onboardingStep !== OnboardingStep.OrganizationCreated;
        // A remembered browser skipped the code, but the recovery codes were
        // never confirmed as saved: remind first, then continue where the
        // chain below would have gone.
        if (data.mfaCodesPending) {
          const destination = onboardingPending
            ? ROUTES.SELF_SIGNUP
            : redirectUrl && redirectUrl.startsWith("/")
              ? redirectUrl
              : ROUTES.ACTIVITY;
          navigate(codesReminderPath(destination));
          return;
        }
        // Additional logic for self-registered users not fully onboarded
        if (onboardingPending) {
          navigate(ROUTES.SELF_SIGNUP);
        } else if (redirectUrl && redirectUrl.startsWith("/")) {
          // A /api/* target (e.g. the OAuth consent resume) is served by the
          // backend, not the SPA router — react-router navigate() can't reach
          // it, so do a full-page navigation carrying the now-set session cookie.
          if (isBackendRoute(redirectUrl)) {
            window.location.assign(redirectUrl);
          } else {
            navigate(redirectUrl);
          }
          setNotification({
            id: "login-success",
            message: "Login Successful",
            variant: NOTIFICATION.SUCCESS,
          });
        } else {
          navigate(ROUTES.ACTIVITY);
          setNotification({
            id: "login-success",
            message: "Login Successful",
            variant: NOTIFICATION.SUCCESS,
          });
        }
      }, 0);
    },
    onError: error => {
      throw error;
    },
  });
};

export const useLogout = (): any => {
  const queryClient = useQueryClient();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (): Promise<any> => {
      return await axios.post("/api/auth/v1/logout");
    },
    onSuccess: async () => {
      // Backend tracks USER_LOGOUT event
      // Clear the entire cache on logout
      queryClient.clear();
      setUser(false);
      navigate(ROUTES.LOGIN);
    },
    onError: error => {
      throw error;
    },
  });
};

export const useForgotPassword = (): any => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (payload: ForgotPasswordPayload) => {
      return await axios.post("/api/auth/v1/forgot-password", payload);
    },
    onSuccess: () => {
      setNotification({
        id: "password-reset-request-success",
        message: "Password Reset Request Successful",
        details: "Recovery link sent. Returning to login page.",
        variant: NOTIFICATION.SUCCESS,
      });
      navigate(ROUTES.LOGIN);
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "password-reset-request-failed",
        message: "Password Reset Request Failed",
        details:
          error?.response?.data?.message || "Recovery link could not be sent",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useVerifyPasswordResetToken = (): any => {
  const navigate = useNavigate();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: VerifyPasswordResetTokenPayload) => {
      return await axios.post(
        "/api/auth/v1/verify-password-reset-token",
        payload
      );
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "invalid-password-reset-token",
        message: "Invalid Password Reset Token",
        details: error?.response?.data || "Invalid Password Reset Token",
        variant: NOTIFICATION.ERROR,
      });
      navigate(ROUTES.LOGIN);
      throw error;
    },
  });
};

export const useSetNewPassword = (): any => {
  const navigate = useNavigate();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: SetNewPasswordPayload) => {
      return await axios.post("/api/auth/v1/set-new-password", payload);
    },
    onSuccess: () => {
      setNotification({
        id: "password-reset-success",
        message: "Password Reset Successful",
        details: "You may now login with your new password",
        variant: NOTIFICATION.SUCCESS,
      });
      navigate(ROUTES.LOGIN);
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "password-reset-failed",
        message: "Password Reset Failed",
        details: error?.response?.data || "Password could not be reset",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

const fetchAuthUser = async (): Promise<boolean | UserInfoResponse | null> => {
  try {
    const response = await axios.get("/api/auth/v1/user");
    return response.data;
  } catch (error) {
    return false;
  }
};

const fetchAuthStatus = async (): Promise<boolean> => {
  try {
    const response = await axios.get("/api/auth/v1/status");
    return response.data.authenticated;
  } catch (error) {
    return false;
  }
};

export const useCheckAuthStatus = (): UseQueryResult<boolean, Error> => {
  return useQuery<boolean, Error>({
    queryKey: ["/api/auth/v1/status"],
    queryFn: fetchAuthStatus,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const useCheckAuthenticated = (authenticated: boolean | null) => {
  return useQuery({
    queryKey: ["/api/auth/v1/user"],
    queryFn: fetchAuthUser,
    refetchOnWindowFocus: true,
    retry: 2,
    enabled: !!authenticated,
  });
};

export const useChangePassword = (): any => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const { mutate: logout } = useLogout();
  return useMutation({
    mutationFn: async (payload: ChangeUserPasswordRequestBody) => {
      const response = await axios.post(
        "/api/auth/v1/change-password",
        payload
      );
      return response.data;
    },
    onSuccess: (): void => {
      logout();
      setNotification({
        id: "password-change-success",
        message: "Password Change Successful",
        details: "Your password has been updated",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "password-change-failed",
        message: "Password Change Failed",
        details:
          error?.response?.data?.message || "Password could not be updated",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};
